// Payroll inputs: each pay period's hours by type per person from the time entries, flags to fix
// before locking, the period's total gross and commissions for everyone who prepares payroll (Q42),
// and the provider file and sending it. Individual pay shows when the server gives it, to whoever
// holds payroll.view (the owner, or by the owner's grant: QA-266), not by role. In Blueprint the
// period's timesheets are Hours' (one Timesheet); Riverstone keeps its own here.

import { useEffect, useState } from 'react';
import { Link } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import type { BooksProps } from './props.ts';
import { dayText, hours } from './model.ts';

/** How payroll.prepare's "no start date" flags start (packages/modules payroll/leave.ts). */
const NO_START_DATE = 'No start date:';
import type { PayPeriod, PayPeriodSummary } from './types.ts';
import { Empty, Loading, Money, Panel, errorText, useAct, useQuery } from './ui.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { useDesign } from '../blueprint/design.ts';
import { Timesheet } from './Timesheet.tsx';
import {
  EnterRegister,
  OpenPeriod,
  PayAndCommissions,
  PeriodCommissions,
  RecordFunding,
  StillToRemit,
} from './PayrollSetup.tsx';
import { fundingWords } from './payroll-model.ts';

const STATUS: Record<PayPeriodSummary['status'], [string, string]> = {
  open: ['Open', ''],
  locked: ['Hours locked', 'brand'],
  sent: ['Sent to the provider', 'brand'],
  posted: ['Posted', 'ok'],
};
function PeriodView({
  id,
  owner,
  timeZone,
  toast,
  onFunded,
  onStaleness,
}: {
  id: string;
  owner: boolean;
  timeZone?: string;
  toast: (m: string) => void;
  /** A funding the company remits for adds to what's still to send CRA. */
  onFunded: () => void;
  /** Whether hours changed since it was prepared, as the server says: the timesheet says so too. */
  onStaleness: (stale: boolean) => void;
}) {
  const p = useQuery<PayPeriod>('payPeriods.get', { id });
  const bp = useDesign() === 'blueprint';
  const stale = !!p.data?.stale;
  // said once it's read (not while a re-read loads), so the timesheet's notice doesn't flicker
  const known = p.data ? stale : null;
  useEffect(() => {
    if (known !== null) onStaleness(known);
  }, [known, onStaleness]);
  // A commissions lock or a funding changes the period's totals: it reads again (gap #12).
  const [version, setVersion] = useState(0);
  // a missing start date doesn't hold up the lock: the office confirms it by name (BL-014)
  const [withoutLeave, setWithoutLeave] = useState(false);
  const act = useAct(toast, () => {
    setVersion((v) => v + 1);
    return p.reload();
  });
  if (!p.data)
    return <Loading error={p.error} what="the pay period" kind="table" retry={() => void p.reload()} />;
  const d = p.data;
  const flags = d.lines.flatMap((l) => l.flags.map((f) => `${l.name}: ${f}`));
  const noStart = d.lines.filter((l) => l.flags.some((f) => f.startsWith(NO_START_DATE)));
  const blocking = d.lines.some((l) => l.flags.some((f) => !f.startsWith(NO_START_DATE)));
  // Individual pay comes only to payroll.view holders; the totals come to everyone here.
  const seesPay = d.lines.some((l) => l.grossCents !== undefined);
  const pay = (c: number | undefined) => (c === undefined ? '—' : <Money cents={c} />);
  const exportFile = async () => {
    try {
      const f = await client.call<{ fileName: string; csv: string }>('payroll.exportFile', { periodId: id });
      const url = URL.createObjectURL(new Blob([f.csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast(errorText(e));
    }
  };
  // Blueprint: the period's commissions and entering the provider's register are sections of their
  // own, so the period's keeps its two actions (DESIGN.md §3: at most two buttons a section)
  const commissions = (
    <PeriodCommissions
      periodId={id}
      open={d.status === 'open'}
      toast={toast}
      version={version}
      onChanged={() => void p.reload()}
    />
  );
  const register = (d.status === 'locked' || d.status === 'sent') && (
    <EnterRegister key={d.id} period={d} toast={toast} onDone={() => void p.reload()} />
  );
  const period = (
    <Panel
      title={`${dayText(d.startDate)} – ${dayText(d.endDate)}`}
      aside={<span className={`chip say ${STATUS[d.status][1]}`}>{STATUS[d.status][0]}</span>}
    >
      <p className="muted small">Pays {dayText(d.payDate)}</p>
      {d.stale && (
        <p className="notice warn" role="status">
          Hours changed since this was prepared (a correction in the timesheet). Prepare it again before
          locking.
        </p>
      )}
      {d.lines.length === 0 ? (
        <Empty>
          {d.status === 'open'
            ? 'No hours yet. Prepare reads the period’s time entries.'
            : 'No one was paid hours in this period.'}
        </Empty>
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table">
            <thead>
              <tr>
                <th>Person</th>
                <th className="num">Work</th>
                <th className="num">Drive</th>
                <th className="num">Shop</th>
                <th className="num">Regular</th>
                <th className="num">1.5×</th>
                <th className="num">2×</th>
                <th className="num">Stat worked</th>
                {seesPay && <th className="num">Stat &amp; sick</th>}
                {seesPay && <th className="num">Commission</th>}
                {seesPay && <th className="num">Gross</th>}
              </tr>
            </thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.staffId} className={l.flags.length ? 'flagged' : ''}>
                  <td>{l.name}</td>
                  <td className="num">{hours(l.workMinutes)}</td>
                  <td className="num">{hours(l.driveMinutes)}</td>
                  <td className="num">{hours(l.nonJobMinutes)}</td>
                  <td className="num">{hours(l.regularMinutes)}</td>
                  <td className="num">{hours(l.overtimeMinutes)}</td>
                  <td className="num">{hours(l.doubleMinutes)}</td>
                  <td className="num">{hours(l.statMinutes + l.statDoubleMinutes)}</td>
                  {seesPay && (
                    <>
                      <td className="num">{pay(l.leaveCents)}</td>
                      <td className="num">
                        {pay(
                          l.commissionsCents === undefined
                            ? undefined
                            : l.commissionsCents + (l.spiffsCents ?? 0),
                        )}
                      </td>
                      <td className="num">{pay(l.grossCents)}</td>
                    </>
                  )}
                </tr>
              ))}
              <tr className="sum">
                <td>Total</td>
                <td colSpan={3} />
                <td className="num">{hours(d.totals.regularMinutes)}</td>
                <td className="num">{hours(d.totals.overtimeMinutes)}</td>
                <td className="num">{hours(d.totals.doubleMinutes)}</td>
                <td />
                {seesPay && (
                  <>
                    <td />
                    <td className="num">
                      <Money cents={d.totals.commissionsCents} />
                    </td>
                    <td className="num">
                      <Money cents={d.totals.grossCents} />
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {d.lines.some((l) => l.leave.length) && (
        <>
          <h3 className="h4">Statutory holidays and sick days</h3>
          <ul className="small" aria-label="Statutory holidays and sick days">
            {d.lines.flatMap((l) =>
              l.leave.map((x) => (
                <li key={`${l.staffId}-${x.date}-${x.kind}`}>
                  {l.name}, {dayText(x.date)}: {x.kind === 'stat' ? 'statutory holiday' : 'sick'}
                  {x.amountCents ? (
                    <>
                      {' '}
                      · <Money cents={x.amountCents} />
                      {x.averageDayCents && x.averageDayCents !== x.amountCents ? (
                        <>
                          {' '}
                          of an average day of <Money cents={x.averageDayCents} />
                        </>
                      ) : (
                        ' (an average day)'
                      )}
                    </>
                  ) : null}
                  {x.note ? ` · ${x.note}` : ''}
                </li>
              )),
            )}
          </ul>
        </>
      )}
      {!seesPay && d.lines.length > 0 && (
        <p className="small">
          This period: commissions <Money cents={d.totals.commissionsCents} /> · total pay{' '}
          <Money cents={d.totals.grossCents} />. Each person’s pay shows to the owner, or to whoever the owner
          gave payroll to.
        </p>
      )}
      {!bp && commissions}
      {flags.length > 0 && (
        <ul className="checks">
          {flags.map((f) => (
            <li key={f} className="bad">
              <span aria-hidden="true">!</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      {d.status === 'open' && !blocking && noStart.length > 0 && (
        <label className="checkrow wide">
          <input type="checkbox" checked={withoutLeave} onChange={(e) => setWithoutLeave(e.target.checked)} />
          <span>
            Lock without stat and sick pay for: {noStart.map((l) => `${l.name} (no start date)`).join(', ')}.
            Once a start date is set, the next open period pays what they’re owed.
          </span>
        </label>
      )}
      <div className="row-actions">
        {d.status === 'open' && (
          <>
            <button
              className="btn"
              disabled={act.busy}
              onClick={() =>
                void act.run('payroll.prepare', { periodId: id }, 'Prepared from the time entries.')
              }
            >
              Prepare
            </button>
            <button
              className="btn primary"
              // a stale period is refused until it's prepared again (QA-400)
              disabled={
                act.busy || blocking || (noStart.length > 0 && !withoutLeave) || d.lines.length === 0 || stale
              }
              onClick={() =>
                void act.run(
                  'payroll.lockHours',
                  {
                    periodId: id,
                    ...(noStart.length ? { withoutStatAndSickFor: noStart.map((l) => l.staffId) } : {}),
                  },
                  'Hours locked.',
                )
              }
            >
              Lock hours
            </button>
          </>
        )}
        {d.status === 'locked' &&
          (owner || seesPay ? (
            <>
              {seesPay && (
                <button className="btn" onClick={() => void exportFile()}>
                  Download the provider file
                </button>
              )}
              {owner && (
                <button
                  className="btn primary"
                  disabled={act.busy}
                  onClick={() =>
                    void act.run('payroll.sendToProvider', { periodId: id }, 'Sent to the payroll provider.')
                  }
                >
                  Send to the provider
                </button>
              )}
            </>
          ) : (
            <span className="muted small">Locked: the owner reviews and sends it (Q42).</span>
          ))}
        {/* sent: the provider's pay register posts it (payroll.post, from the provider); then the money
            it took from the bank is recorded here */}
        {d.status === 'sent' && (
          <span className="muted small">
            Sent: it posts when the provider’s pay register comes in, or when it’s entered here. Then record
            what the provider took from the bank.
          </span>
        )}
        {d.status === 'posted' && d.funding && (
          <span className="small" data-testid="payroll-funded">
            {fundingWords(d.funding)}
          </span>
        )}
        {d.status === 'posted' && !d.funding && (
          <RecordFunding
            period={d}
            timeZone={timeZone}
            toast={toast}
            onDone={() => {
              onFunded();
              void p.reload();
            }}
          />
        )}
      </div>
      {!bp && register}
    </Panel>
  );
  if (!bp) return period;
  return (
    <>
      {period}
      <section className="sheet panel" aria-label="Commissions">
        {commissions}
      </section>
      {register && <Panel title="The provider’s register">{register}</Panel>}
    </>
  );
}

export function Payroll({ held, toast, timeZone, focus, onHours }: BooksProps) {
  const bp = useDesign() === 'blueprint';
  const list = useQuery<{ periods: PayPeriodSummary[] }>('payPeriods.list', {});
  // A pay period's link (Overview's books check, say) opens that period.
  const [chosen, setChosen] = useState<string | null>(focus ?? null);
  // A timesheet correction in a prepared period makes it stale: the period view reads it again.
  const [staleBump, setStaleBump] = useState(0);
  const [periodStale, setPeriodStale] = useState(false);
  const [funded, setFunded] = useState(0);
  // the owner reviews and sends it (payroll.send: the owner's, or a role of theirs that gives it)
  const owner = held.includes('payroll.send');
  if (!list.data)
    return <Loading error={list.error} what="the pay periods" retry={() => void list.reload()} />;
  const id = chosen ?? list.data.periods[0]?.id ?? null;
  const shown = list.data.periods.find((x) => x.id === id) ?? null;
  const newest = list.data.periods[0] ?? null;
  const opened = (periodId: string) => {
    setChosen(periodId);
    void list.reload();
  };
  return (
    <div className="books-page">
      <div className="page-head">
        {list.data.periods.length > 1 && (
          <div className="seg wrap" role="group" aria-label="Pay period">
            {list.data.periods.slice(0, 6).map((p) => (
              <button
                key={p.id}
                className={`btn quiet${p.id === id ? ' on' : ''}`}
                onClick={() => setChosen(p.id)}
              >
                {dayText(p.startDate)} – {dayText(p.endDate)}
              </button>
            ))}
          </div>
        )}
        {newest && (
          <OpenPeriod
            key={newest.id}
            newest={newest}
            timeZone={timeZone}
            toast={toast}
            onOpened={opened}
            quiet
          />
        )}
      </div>
      {id ? (
        <PeriodView
          key={`${id}-${staleBump}`}
          id={id}
          owner={owner}
          timeZone={timeZone}
          toast={toast}
          onFunded={() => setFunded((n) => n + 1)}
          onStaleness={setPeriodStale}
        />
      ) : (
        <OpenPeriod newest={null} timeZone={timeZone} toast={toast} onOpened={opened} />
      )}
      <StillToRemit timeZone={timeZone} toast={toast} version={funded} />
      {shown && bp && onHours ? (
        // Blueprint has one timesheet (timesheet rules §4, §5): the period's, on Hours
        <Panel title="Timesheet">
          <p className="muted">
            The technicians’ hours for {dayText(shown.startDate)} – {dayText(shown.endDate)} are fixed and
            locked on Hours, a timesheet per person.
          </p>
          <p>
            <Link onPress={() => onHours(shown.startDate)}>Open the timesheets ›</Link>
          </p>
        </Panel>
      ) : shown ? (
        <Timesheet
          range={{ from: shown.startDate, to: shown.endDate }}
          timeZone={timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
          toast={toast}
          stale={periodStale}
          onStale={() => setStaleBump((n) => n + 1)}
        />
      ) : null}
      {owner && <PayAndCommissions toast={toast} />}
    </div>
  );
}
